import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';

// "+91 9876543210" for display; the dial code is left off when the number already carries one.
export const formatPhone = (phoneNumber, countryCode) => {
  const phone = phoneNumber ? String(phoneNumber).trim() : '';
  const code = countryCode ? String(countryCode).trim() : '';
  if (!phone) return '';
  return code && !phone.startsWith('+') ? `${code} ${phone}` : phone;
};

// The copied form is the number alone, without the dial code or spaces ("9876543210"), so it pastes
// straight into a phone-number field that has its own country picker. A number stored with its dial
// code ("+919876543210") has that code taken off; one whose code isn't known is copied whole.
export const copyValue = (phoneNumber, countryCode) => {
  const phone = phoneNumber ? String(phoneNumber).replace(/\s+/g, '') : '';
  const code = countryCode ? String(countryCode).replace(/\s+/g, '') : '';
  if (!phone.startsWith('+') || !code) return phone;
  const dialCode = code.startsWith('+') ? code : `+${code}`;
  return phone.startsWith(dialCode) ? phone.slice(dialCode.length) : phone;
};

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Older browsers and non-secure origins have no async clipboard API.
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  document.execCommand('copy');
  document.body.removeChild(field);
}

// A table cell's phone number with a quiet copy button beside it. The button stays faint until the
// row is hovered or the button is focused, so a column of numbers doesn't read as a column of icons.
export default function CopyablePhone({ phoneNumber, countryCode, textSx }) {
  const display = formatPhone(phoneNumber, countryCode);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!display) {
    return <Typography sx={textSx}>-</Typography>;
  }

  const handleCopy = async (event) => {
    // The click must not reach the grid: it would select or open the row.
    event.stopPropagation();
    try {
      await writeClipboard(copyValue(phoneNumber, countryCode));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        '.MuiDataGrid-row:hover & .copy-phone-button': { opacity: 1 },
      }}
    >
      <Typography noWrap sx={textSx}>{display}</Typography>
      <Tooltip title={copied ? 'Copied' : 'Copy phone number'} placement="top" arrow>
        <IconButton
          className="copy-phone-button"
          size="small"
          aria-label={`Copy phone number ${display}`}
          onClick={handleCopy}
          onMouseDown={(event) => event.stopPropagation()}
          sx={{
            p: 0.5,
            color: copied ? '#4F8506' : '#6C6A86',
            opacity: copied ? 1 : 0.45,
            transition: 'opacity 120ms, color 120ms',
            '&:hover, &:focus-visible': { opacity: 1, color: '#4F8506', backgroundColor: 'transparent' },
          }}
        >
          <Icon
            icon={copied ? 'material-symbols:check-rounded' : 'material-symbols:content-copy-outline-rounded'}
            width="16"
            height="16"
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
